/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { NGXLogger } from 'ngx-logger';
import { Platform } from '@ionic/angular';
import { API, HeImage } from '@app/core/models';
import { CoreDataService } from '../core.data';
import { firstValueFrom, forkJoin, Observable } from 'rxjs';
import {
  CameraResultType,
  CameraSource,
  ImageOptions,
} from '@capacitor/camera';
import { CameraService } from '@app/core/services/camera/camera.service';
import { DomSanitizer } from '@angular/platform-browser';
import { CapacitorService } from '@app/core/services/capacitor/capacitor.service';
import { HttpService } from '@app/core/services/http/http.service';
import { FileSystemService } from '@app/core/services/file-system/file-system.service';

@Injectable({
  providedIn: 'root',
})
export class ImageService {
  imagesBaseUrl!: string;

  constructor(
    private _logger: NGXLogger,
    private _platform: Platform,
    private _coreDataService: CoreDataService,
    private cameraService: CameraService,
    private _sanitizer: DomSanitizer,
    private capacitorService: CapacitorService,
    private httpService: HttpService,
    private fileSystemService: FileSystemService,
  ) {}

  async uploadImages(
    images: Array<HeImage>,
    urlPrefix: string,
    endpoint: string,
  ): Promise<{ secureUrls: string[] }> {
    if (images.length < 1) {
      return { secureUrls: [] };
    }

    const secureUrlSubs = images.map((image) =>
      this._getSecureUrl(image.fileName, urlPrefix, endpoint),
    );
    const secureUrlResponses = await firstValueFrom(forkJoin(secureUrlSubs));
    images.forEach((image, index) => {
      const payload = secureUrlResponses[index].payload;
      if (payload !== undefined) {
        image.secureUrl = payload.url;
      }
    });
    await this._putToSecureUrls(images);
    this._logger.debug('_putToSecureUrls DONE');
    return { secureUrls: images.map((image) => image.secureUrl) };
  }

  private _getSecureUrl(
    imageName: string,
    urlPrefix: string,
    endpoint: string,
  ): Observable<API.SecureUrlResponse> {
    const options: API.HttpOptions = !this._platform.is('capacitor')
      ? {
          headers: new HttpHeaders(),
          withCredentials: true,
        }
      : {};

    const body = {
      fileName: this.stripFileName(imageName),
    };

    return this._coreDataService.post<API.SecureUrlResponse>(
      urlPrefix + endpoint,
      body,
      options,
    );
  }

  stripFileName(fileName: string): string | undefined {
    // Android sometimes puts a question mark and a number
    // ie. '?12341234' after the filename.
    // This creates a problem because it changes the secure url
    // and we can't authenticate when putting the image to Amazon.
    // This function removes the extraneous data.
    return fileName.split('?').shift();
  }

  private async _putToSecureUrls(images: Array<HeImage>): Promise<void> {
    await Promise.all(
      images.map((image) => this._readFileFromDeviceAndSendToAmazon(image)),
    );
  }

  private async _readFileFromDeviceAndSendToAmazon({
    filePath,
    secureUrl,
    fileName,
  }: HeImage): Promise<void> {
    const { data } = await this.fileSystemService.readFile({ path: filePath });
    const contentType = `image/${fileName.split('.').pop()}`;
    //  Blob is only available on Web. On native, the data is returned as a string.
    await this._putToAmazon(secureUrl, data as string, contentType);
    this._logger.debug('_putToAmazon DONE');
  }

  private _putToAmazon(
    secureUrl: string,
    base64Image: string,
    contentType: string,
  ): Promise<any> {
    return this.httpService.request({
      url: secureUrl,
      method: 'PUT',
      data: base64Image,
      headers: {
        'Content-Type': contentType,
      },
      dataType: 'file',
    });
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  async newImage(): Promise<HeImage> {
    if (!this._platform.is('capacitor')) {
      alert('Image upload is not yet supported for web.');
      throw new Error('Image upload is not yet supported for web.');
    }

    const delay = new Promise<void>((resolve) => setTimeout(resolve, 50));
    await delay;

    const cameraOptions: ImageOptions = {
      source: CameraSource.Photos,
      // Some common settings are 20, 50, and 100
      quality: 50,
      resultType: CameraResultType.Uri,
      correctOrientation: true, // Corrects Android orientation quirks
      height: 1080,
      width: 1080,
    };

    const photo = await this.cameraService.getPhoto(cameraOptions);
    const imagePath: string = photo.path!;
    const convertedImagePath = this.capacitorService.convertFileSrc(imagePath);

    const image: HeImage = {
      filePath: imagePath,
      fileName: imagePath.split('/').pop()!,
      convertedImagePath,
      trustedConvertedImagePath:
        this._sanitizer.bypassSecurityTrustUrl(convertedImagePath),
      secureUrl: '',
    };

    return image;
  }

  deleteImage(images: Array<HeImage>, filePath: string): void {
    const index = images.findIndex((image) => image.filePath === filePath);
    if (index !== -1) {
      images.splice(index, 1);
    }
  }

  getSecureUrls(images: Array<HeImage>) {
    return images.map((image) => image.secureUrl);
  }
}
